/**
 * TSGo notification bell — shared across all four PWAs.
 *
 * Reads from /api/notifications (per-user inbox written by
 * `notifications.notify_store_new_order` / `notify_user`). Every user only
 * ever sees rows keyed on their own _id, so Store A can never load Store B's
 * inbox and a customer can never load an admin's. Server enforces that.
 *
 * Behaviour
 * ---------
 * - Polls unread count every 30s.
 * - Full list fetched on open.
 * - Tapping a row marks it read and follows the deep-link when the row
 *   carries a known `data.order_id`.
 * - "Mark all read" collapses the badge instantly.
 */

import { useEffect, useRef, useState } from "react";
import { Bell, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/features/auth/api";
import { cn } from "@/lib/utils";

type Notif = {
    id: string;
    kind: string;
    title: string;
    body: string;
    data: Record<string, unknown>;
    read_at: string | null;
    created_at: string;
};

type InboxResponse = { items: Notif[]; unread: number };

function timeAgo(iso: string): string {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    const secs = Math.max(1, Math.floor((Date.now() - t) / 1000));
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
}

export function NotificationBell({ className }: { className?: string }) {
    const [open, setOpen] = useState(false);
    const [inbox, setInbox] = useState<InboxResponse>({ items: [], unread: 0 });
    const timerRef = useRef<number | null>(null);
    const navigate = useNavigate();

    const load = async () => {
        try {
            const { data } = await api.get<InboxResponse>("/notifications");
            setInbox(data);
        } catch {
            /* silent — unauthenticated screens surface no bell UI */
        }
    };

    const pollUnread = async () => {
        try {
            const { data } = await api.get<{ unread: number }>("/notifications/unread");
            setInbox((s) => ({ ...s, unread: data.unread }));
        } catch {
            /* silent */
        }
    };

    useEffect(() => {
        pollUnread();
        timerRef.current = window.setInterval(pollUnread, 30000);
        return () => {
            if (timerRef.current) window.clearInterval(timerRef.current);
        };
    }, []);

    useEffect(() => {
        if (open) load();
    }, [open]);

    const markRead = async (ids: string[]) => {
        try {
            await api.post("/notifications/read", { ids });
        } catch {
            /* silent */
        }
    };

    const markAll = async () => {
        try {
            await api.post("/notifications/read", { all: true });
            setInbox((s) => ({
                unread: 0,
                items: s.items.map((n) => ({ ...n, read_at: n.read_at ?? new Date().toISOString() })),
            }));
        } catch {
            /* silent */
        }
    };

    const onOpen = (n: Notif) => {
        if (!n.read_at) {
            markRead([n.id]);
            setInbox((s) => ({
                unread: Math.max(0, s.unread - 1),
                items: s.items.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)),
            }));
        }
        const orderId = (n.data as { order_id?: string })?.order_id;
        if (orderId) {
            setOpen(false);
            navigate(`/admin/orders/${orderId}`);
        }
    };

    return (
        <div className={cn("relative", className)} data-testid="notification-bell">
            <button
                type="button"
                aria-label="Notifications"
                data-testid="notification-bell-button"
                onClick={() => setOpen((v) => !v)}
                className="relative grid h-9 w-9 place-items-center rounded-full border border-border/70 bg-background text-foreground/80 transition hover:border-brand hover:text-brand"
            >
                <Bell className="h-4 w-4" />
                {inbox.unread > 0 ? (
                    <span
                        data-testid="notification-unread-badge"
                        className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-error px-1 text-[10px] font-bold text-white"
                    >
                        {inbox.unread > 99 ? "99+" : inbox.unread}
                    </span>
                ) : null}
            </button>
            {open ? (
                <>
                    <button
                        aria-label="Close notifications"
                        className="fixed inset-0 z-40 cursor-default bg-transparent"
                        onClick={() => setOpen(false)}
                    />
                    <div
                        data-testid="notification-panel"
                        className="absolute right-0 z-50 mt-2 w-80 max-w-[92vw] rounded-2xl border border-border/70 bg-card shadow-elevated"
                    >
                        <div className="flex items-center justify-between border-b border-border/70 px-3 py-2">
                            <p className="text-sm font-bold">Notifications</p>
                            <button
                                type="button"
                                onClick={markAll}
                                disabled={inbox.unread === 0}
                                data-testid="notification-mark-all"
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand disabled:opacity-40"
                            >
                                <Check className="h-3 w-3" /> Mark all read
                            </button>
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                            {inbox.items.length === 0 ? (
                                <p className="px-3 py-6 text-center text-xs text-muted-foreground">
                                    You're all caught up.
                                </p>
                            ) : (
                                <ul className="divide-y divide-border/60">
                                    {inbox.items.map((n) => (
                                        <li key={n.id}>
                                            <button
                                                type="button"
                                                onClick={() => onOpen(n)}
                                                data-testid={`notification-item-${n.id}`}
                                                className={cn(
                                                    "flex w-full items-start gap-2 px-3 py-2 text-left transition hover:bg-muted",
                                                    !n.read_at && "bg-brand/5",
                                                )}
                                            >
                                                <span
                                                    className={cn(
                                                        "mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full",
                                                        !n.read_at ? "bg-brand" : "bg-transparent",
                                                    )}
                                                />
                                                <span className="flex-1">
                                                    <span className="block text-xs font-bold">{n.title}</span>
                                                    <span className="block text-xs text-muted-foreground">
                                                        {n.body}
                                                    </span>
                                                    <span className="mt-0.5 block text-[10px] text-muted-foreground">
                                                        {timeAgo(n.created_at)}
                                                    </span>
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    );
}

export default NotificationBell;
